/**
 * Sentry init for the Pro / Marketing SPA (Vite + React). Called once
 * from main.tsx before React renders. No-ops if VITE_SENTRY_DSN is not
 * set — that keeps `npm run dev` and untokenised CI builds working
 * without surfacing an init error in the browser console.
 */
import * as Sentry from '@sentry/react';

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    // Static app tag so the consolidated `golfauth` Sentry project
    // can filter Fitting vs Core events (2026-07-06). runtime is
    // always 'web' here — Fitting isn't a native app.
    initialScope: {
      tags: { app: 'fitting', runtime: 'web' },
    },
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
    beforeSend(event) {
      if (import.meta.env.DEV) return null;
      if (event.user) {
        delete event.user.email;
        delete event.user.ip_address;
      }
      // Drop bare SW-registration failures still coming from older
      // deployed `/registerSW.js` (no `.catch`) until that cohort rolls
      // off. Matches Sentry 5aa2f674… — stack frames reference
      // registerSW.js / ServiceWorkerContainer.register, value "Rejected".
      if (isBareServiceWorkerRegisterRejection(event)) {
        return null;
      }
      // Browser extension / password-manager noise (Chrome "Object Not
      // Found Matching Id:… MethodName:update"). Not application code —
      // Sentry GOLFAUTH-K on /customer/login.
      if (isBrowserExtensionObjectNotFoundRejection(event)) {
        return null;
      }
      // Facebook / Instagram in-app browser noise. The app injects its
      // own scripts (iabjs://navigation_performance_logger_android etc.)
      // and they throw "Java object is gone" once the native bridge is
      // torn down. Not application code — Sentry 7822987392 on /.
      if (isInAppBrowserInjectedScriptError(event)) {
        return null;
      }
      return event;
    },
  });
}

function isBareServiceWorkerRegisterRejection(
  event: Sentry.ErrorEvent,
): boolean {
  const exception = event.exception?.values?.[0];
  if (!exception) return false;
  const value = (exception.value ?? '').trim();
  if (value !== 'Rejected' && value !== 'Error: Rejected') return false;

  const frames = exception.stacktrace?.frames ?? [];
  return frames.some((frame) => {
    const filename = frame.filename ?? '';
    const fn = frame.function ?? '';
    return (
      filename.includes('registerSW.js') ||
      fn.includes('ServiceWorkerContainer') ||
      fn.includes('serviceWorker.register')
    );
  });
}

function isBrowserExtensionObjectNotFoundRejection(
  event: Sentry.ErrorEvent,
): boolean {
  const exception = event.exception?.values?.[0];
  if (!exception) return false;
  const value = exception.value ?? '';
  return /Object Not Found Matching Id:\d+/i.test(value);
}

function isInAppBrowserInjectedScriptError(event: Sentry.ErrorEvent): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
  // Sentry orders frames oldest-first, so the last one is where it threw.
  const top = frames[frames.length - 1];
  return (top?.filename ?? '').startsWith('iabjs://');
}
